import { useState } from "react";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import {
	FileText,
	Upload,
	Download,
	XCircle,
	AlertTriangle,
	Eraser,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useFileUpload } from "../../../hooks/useFileUpload";

const PDF_MAGIC = "%PDF";

const decodeBase64 = (input: string): Uint8Array => {
	const binary = atob(input.replace(/\s/g, ""));
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
};

const decodeHex = (input: string): Uint8Array => {
	const hex = input.replace(/\s/g, "");
	if (hex.length % 2 !== 0) {
		throw new Error("Hex-Eingabe hat eine ungerade Anzahl Zeichen");
	}
	const bytes = new Uint8Array(hex.length / 2);
	for (let i = 0; i < bytes.length; i++) {
		bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
	}
	return bytes;
};

const decodeByteArray = (values: unknown[]): Uint8Array => {
	const bytes = new Uint8Array(values.length);
	for (let i = 0; i < values.length; i++) {
		const value = values[i];
		if (typeof value !== "number" || value < 0 || value > 255) {
			throw new Error(
				`Ungültiger Byte-Wert an Position ${i}: ${String(value)}`
			);
		}
		bytes[i] = value;
	}
	return bytes;
};

export const decodeBlobInput = (raw: string): Uint8Array => {
	let input = raw.trim();
	if (!input) {
		throw new Error("Eingabe ist leer");
	}

	if (
		(input.startsWith('"') && input.endsWith('"')) ||
		(input.startsWith("'") && input.endsWith("'"))
	) {
		input = input.slice(1, -1).trim();
	}

	const dataUrlMatch = input.match(/^data:[^,;]*;base64,([\s\S]*)$/);
	if (dataUrlMatch) {
		return decodeBase64(dataUrlMatch[1]);
	}

	if (input.startsWith("\\x")) {
		return decodeHex(input.slice(2));
	}

	if (input.startsWith("{")) {
		const parsed = JSON.parse(input) as { type?: string; data?: unknown[] };
		if (parsed.type === "Buffer" && Array.isArray(parsed.data)) {
			return decodeByteArray(parsed.data);
		}
		throw new Error(
			"JSON-Objekt ist kein serialisierter Buffer ({ type: 'Buffer', data: [...] })"
		);
	}

	if (input.startsWith("[")) {
		const parsed = JSON.parse(input) as unknown[];
		if (!Array.isArray(parsed)) {
			throw new Error("JSON-Eingabe ist kein Array");
		}
		return decodeByteArray(parsed);
	}

	const compact = input.replace(/\s/g, "");
	if (/^(0x)?255044462d/i.test(compact)) {
		return decodeHex(compact.replace(/^0x/i, ""));
	}

	try {
		return decodeBase64(compact);
	} catch {
		throw new Error(
			"Format nicht erkannt. Unterstützt: Base64, Data-URL, Hex (\\x...), Byte-Array [37,80,...], Buffer-JSON"
		);
	}
};

const formatBytes = (size: number): string => {
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

interface LoadedPdf {
	url: string;
	size: number;
	isPdf: boolean;
	source: string;
}

export const BlobToPdfPage = () => {
	const [blobInput, setBlobInput] = useState("");
	const [loadedPdf, setLoadedPdf] = useState<LoadedPdf | null>(null);
	const [error, setError] = useState<string | null>(null);

	const showBytes = (bytes: Uint8Array, source: string) => {
		if (loadedPdf) {
			URL.revokeObjectURL(loadedPdf.url);
		}
		const header = new TextDecoder().decode(bytes.slice(0, 4));
		const blob = new Blob([bytes.buffer as ArrayBuffer], {
			type: "application/pdf",
		});
		setLoadedPdf({
			url: URL.createObjectURL(blob),
			size: bytes.length,
			isPdf: header === PDF_MAGIC,
			source,
		});
		setError(null);
	};

	const handleRender = () => {
		try {
			showBytes(decodeBlobInput(blobInput), "Eingabefeld");
		} catch (e) {
			setError(
				e instanceof Error
					? e.message
					: "Eingabe konnte nicht dekodiert werden"
			);
		}
	};

	const handleFile = async (file: File) => {
		const buffer = await file.arrayBuffer();
		showBytes(new Uint8Array(buffer), file.name);
	};

	const handleClear = () => {
		if (loadedPdf) {
			URL.revokeObjectURL(loadedPdf.url);
		}
		setLoadedPdf(null);
		setBlobInput("");
		setError(null);
	};

	const { isDragging, dragHandlers } = useFileUpload({
		onFileUpload: (files) => {
			if (files.length > 0) {
				handleFile(files[0]);
			}
		},
	});

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[
						{ name: "Support", link: "/support" },
						{ name: "Blob zu PDF" },
					]}
					icon={
						<FileText
							width="16px"
							height="16px"
							color="rgb(55, 65, 81)"
						/>
					}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-bold text-gray-900">
						Blob zu PDF
					</h1>
					<p className="text-gray-600">
						Wandelt rohe Blob-Daten in ein gerendertes PDF um.
						Unterstützt Base64, Data-URLs, Postgres-Bytea-Hex
						(\x...), Byte-Arrays und serialisierte Buffer-Objekte —
						oder eine Datei per Drag & Drop.
					</p>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<div className="flex flex-col gap-4">
						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="text-lg font-semibold mb-4">
								Blob-Eingabe
							</h3>
							<textarea
								value={blobInput}
								onChange={(e) => {
									setBlobInput(e.target.value);
									setError(null);
								}}
								className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md font-mono text-xs"
								placeholder={
									'JVBERi0xLjcK... (Base64)\ndata:application/pdf;base64,...\n\\x255044462d...\n[37, 80, 68, 70, ...]\n{"type":"Buffer","data":[37,80,...]}'
								}
								data-testid="blob-input"
								aria-label="Blob-Daten Eingabe"
							/>
							{error && (
								<div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
									<XCircle className="w-4 h-4 shrink-0" />
									{error}
								</div>
							)}
							<div className="flex gap-2 mt-4">
								<Button
									onClick={handleRender}
									disabled={!blobInput.trim()}
									className="flex-1"
									data-testid="render-pdf-button"
									aria-label="Blob als PDF rendern"
								>
									<FileText className="w-4 h-4 mr-2" />
									PDF rendern
								</Button>
								<Button
									onClick={handleClear}
									variant="outline"
									disabled={!blobInput && !loadedPdf}
									data-testid="clear-button"
									aria-label="Eingabe und Vorschau zurücksetzen"
								>
									<Eraser className="w-4 h-4 mr-2" />
									Zurücksetzen
								</Button>
							</div>
						</div>

						<div
							className={`bg-white border-2 border-dashed rounded-lg p-8 transition-all ${
								isDragging
									? "border-blue-500 bg-blue-50"
									: "border-gray-300 hover:border-blue-400"
							}`}
							{...dragHandlers}
						>
							<div className="flex flex-col items-center justify-center gap-3">
								<Upload
									className={`w-10 h-10 ${
										isDragging
											? "text-blue-500"
											: "text-gray-400"
									}`}
								/>
								<p className="text-sm text-gray-600">
									{isDragging
										? "Datei hier ablegen..."
										: "Alternativ: Blob-Datei hierher ziehen"}
								</p>
								<label>
									<input
										type="file"
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) {
												handleFile(file);
											}
											e.target.value = "";
										}}
										className="hidden"
										aria-label="Blob-Datei auswählen"
										data-testid="blob-file-input"
									/>
									<Button
										type="button"
										variant="outline"
										size="sm"
										aria-label="Blob-Datei auswählen"
										data-testid="blob-file-button"
										onClick={(e) => {
											e.preventDefault();
											(
												e.currentTarget
													.previousElementSibling as HTMLInputElement
											)?.click();
										}}
									>
										Datei auswählen
									</Button>
								</label>
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col gap-4 min-h-[500px]">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold">
									Vorschau
								</h3>
								{loadedPdf && (
									<div className="flex items-center gap-3">
										<span className="text-sm text-gray-500">
											{loadedPdf.source} ·{" "}
											{formatBytes(loadedPdf.size)}
										</span>
										<a
											href={loadedPdf.url}
											download="blob.pdf"
											aria-label="PDF herunterladen"
											data-testid="download-pdf-button"
										>
											<Button variant="outline" size="sm">
												<Download className="w-4 h-4 mr-2" />
												Herunterladen
											</Button>
										</a>
									</div>
								)}
							</div>

							{loadedPdf && !loadedPdf.isPdf && (
								<div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
									<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-orange-600" />
									Die Daten beginnen nicht mit "%PDF" —
									möglicherweise kein gültiges PDF. Die
									Vorschau kann fehlschlagen.
								</div>
							)}

							{loadedPdf ? (
								<iframe
									src={loadedPdf.url}
									title="PDF Vorschau"
									className="w-full flex-1 min-h-[600px] border border-gray-200 rounded"
									data-testid="pdf-preview-iframe"
								/>
							) : (
								<div className="flex flex-col items-center justify-center flex-1 text-gray-500">
									<FileText className="w-12 h-12 text-gray-300 mb-3" />
									Noch kein PDF geladen
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
